/**
 * Files (the Drive-like area, contracts drive.ts, docs/features/files.md): who may do what (the API enforces the same
 * rules), effective roles from shares on an item and the folders above it, list order (folders first, like Google
 * Drive), the type filter, names for copies and conflicted copies, move validation and storage use.
 * Pure: no I/O, no clock, no randomness.
 */
import type { DriveAnyoneBlocked, DriveItem, DriveItemKind, DriveLinkScope, PublishPolicy, Role, DrivePrincipalKind, DriveRole, DriveSort, DriveTypeFilter } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { fileKind } from "./files.ts";

// ---------- Roles ----------

const RANK: Record<DriveRole, number> = { viewer: 1, commenter: 2, editor: 3, owner: 4 };

/** True when `role` is at least `min` (no role = nothing). */
export function roleAtLeast(role: DriveRole | undefined, min: DriveRole): boolean {
  return role !== undefined && RANK[role] >= RANK[min];
}

/** The higher of two roles (either may be absent). */
export function maxRole(a: DriveRole | undefined, b: DriveRole | undefined): DriveRole | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return RANK[a] >= RANK[b] ? a : b;
}

/** One share as stored on an item: who and with what role. */
export interface DriveGrant {
  principal: DrivePrincipalKind;
  principalId?: string;
  role: DriveRole;
}

/** One item on the way from the item itself up to its top folder: its owner, own shares and link access. */
export interface DriveChainLink {
  ownerId: string;
  grants: DriveGrant[];
  link?: { scope: DriveLinkScope; role: DriveRole };
}

/** Who is asking: their groups and conversations, whether they are a guest, and whether they came with the link. */
export interface DriveViewer {
  userId: string;
  groupIds: string[];
  channelIds: string[];
  /** Guests and agents: "workspace" shares and workspace links don't reach them. */
  restricted: boolean;
  /** Opened through a link (link access counts even if nothing names them). */
  viaLink: boolean;
}

/**
 * The viewer's role on an item. `chain` is the item first, then each folder above it. The owner of the item is
 * "owner"; the owner of a folder above it is an editor of everything inside (Drive). Otherwise the highest role that
 * any share or (via link) link access along the chain gives them. Undefined = no access.
 */
export function effectiveRole(chain: DriveChainLink[], viewer: DriveViewer): DriveRole | undefined {
  if (chain.length === 0) return undefined;
  if (chain[0]!.ownerId === viewer.userId) return "owner";
  let role: DriveRole | undefined;
  for (const link of chain) {
    if (link.ownerId === viewer.userId) role = maxRole(role, "editor");
    for (const g of link.grants) if (grantMatches(g, viewer)) role = maxRole(role, g.role === "owner" ? "editor" : g.role);
    if (viewer.viaLink && link.link !== undefined) role = maxRole(role, roleForLinkVisitor(link.link.scope, link.link.role, !viewer.restricted));
  }
  return role;
}

/**
 * What opening an item through its link gives someone. `isMember`: a full workspace member (not a guest or agent).
 * "workspace" links: the link role, members only. "anyone" links: members get the link role like "workspace"; everyone
 * else (guests, people from other workspaces, signed-out visitors) can only view and download. A link never makes
 * anyone the owner (an "owner" link role reads as editor). Undefined = the link gives nothing.
 */
export function roleForLinkVisitor(scope: DriveLinkScope, role: DriveRole, isMember: boolean): DriveRole | undefined {
  const linkRole: DriveRole = role === "owner" ? "editor" : role;
  switch (scope) {
    case "restricted":
      return undefined;
    case "workspace":
      return isMember ? linkRole : undefined;
    case "anyone":
      return isMember ? linkRole : "viewer";
  }
}

/**
 * May this member choose "Anyone with the link"? It follows the workspace's `publicFiles` policy (public file links):
 * "off" blocks everyone; "admins" lets only owners and admins; "members" every full member (guests and agents never).
 * Null = allowed (they still need to be able to share the item).
 */
export function anyoneLinkBlocked(policy: PublishPolicy, role: Role, restricted: boolean): DriveAnyoneBlocked | null {
  if (policy === "off") return "off";
  if (restricted || role === "guest") return "admins";
  if (policy === "admins" && role !== "owner" && role !== "admin") return "admins";
  return null;
}

/**
 * Can a public visitor of an "anyone" link open `itemId`? `ancestors` are the item's folders from its parent up to the
 * top, `trashed` whether it or any of them is in the trash. Only the shared item itself or something below it, and
 * nothing in the trash.
 */
export function publicLinkReaches(sharedId: string, itemId: string, ancestors: string[], trashed: boolean): boolean {
  if (trashed) return false;
  return itemId === sharedId || ancestors.includes(sharedId);
}

function grantMatches(g: DriveGrant, v: DriveViewer): boolean {
  switch (g.principal) {
    case "user":
      return g.principalId === v.userId;
    case "group":
      return g.principalId !== undefined && v.groupIds.includes(g.principalId);
    case "channel":
      return g.principalId !== undefined && v.channelIds.includes(g.principalId);
    case "workspace":
      return !v.restricted;
  }
}

export type DriveAction =
  | "view"
  | "download"
  | "comment"
  | "rename"
  | "edit"
  | "addVersion"
  | "share"
  | "move"
  | "copy"
  | "trash"
  | "deleteForever"
  | "transferOwnership";

/**
 * May someone with `role` do `action`? Drive's rules: viewers open and download; commenters also comment; editors
 * also rename, upload versions, share, move and trash; deleting forever and transferring ownership are the owner's.
 * Folders can't be copied.
 */
export function driveCan(role: DriveRole | undefined, action: DriveAction, kind: DriveItemKind): boolean {
  switch (action) {
    case "view":
    case "download":
      return roleAtLeast(role, "viewer");
    case "copy":
      return kind !== "folder" && roleAtLeast(role, "viewer");
    case "comment":
      return roleAtLeast(role, "commenter");
    case "rename":
    case "edit":
    case "share":
    case "move":
    case "trash":
      return roleAtLeast(role, "editor");
    case "addVersion":
      return kind === "file" && roleAtLeast(role, "editor");
    case "deleteForever":
    case "transferOwnership":
      return role === "owner";
  }
}

/** Role names in the share dialog's menu, lowest first ("owner" is shown, never picked). */
export function roleLabel(role: DriveRole): MessageDescriptor {
  switch (role) {
    case "viewer":
      return msg("drive.role.viewer");
    case "commenter":
      return msg("drive.role.commenter");
    case "editor":
      return msg("drive.role.editor");
    case "owner":
      return msg("drive.role.owner");
  }
}

// ---------- Types and order ----------

/** The type filter chip / search `type:` an item falls under. */
export function driveTypeOf(item: { kind: DriveItemKind; targetKind?: DriveItemKind; mime?: string; name: string }): DriveTypeFilter {
  const kind = item.kind === "shortcut" ? (item.targetKind ?? "file") : item.kind;
  if (kind === "folder") return "folder";
  if (kind === "page" || kind === "database") return "page";
  switch (fileKind(item.mime ?? "", item.name)) {
    case "document":
    case "text":
      return "document";
    case "spreadsheet":
      return "spreadsheet";
    case "presentation":
      return "presentation";
    case "pdf":
      return "pdf";
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "archive":
      return "archive";
    default:
      return "other";
  }
}

/** Folders (and shortcuts to folders) come first in every list, like Google Drive. */
function isFolderish(i: Pick<DriveItem, "kind" | "targetKind">): boolean {
  return i.kind === "folder" || (i.kind === "shortcut" && i.targetKind === "folder");
}

type SortableItem = Pick<DriveItem, "id" | "kind" | "targetKind" | "name" | "modifiedAt" | "size"> & { viewedAt?: number; sharedAt?: number };

/**
 * List order: folders first, then by `sort` (`dir` "asc" | "desc"). Names compare case-insensitively with numbers in
 * order ("File 2" before "File 10"); ties fall back to the name, then the id, so the order is stable.
 */
export function sortDriveItems<T extends SortableItem>(items: T[], sort: DriveSort, dir: "asc" | "desc"): T[] {
  const sign = dir === "asc" ? 1 : -1;
  const byName = (a: T, b: T) => compareNames(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const key = (i: T): number => {
    switch (sort) {
      case "modified":
        return i.modifiedAt;
      case "viewed":
        return i.viewedAt ?? 0;
      case "size":
        return i.size;
      case "shared":
        return i.sharedAt ?? 0;
      case "name":
        return 0;
    }
  };
  return [...items].sort((a, b) => {
    const fa = isFolderish(a);
    const fb = isFolderish(b);
    if (fa !== fb) return fa ? -1 : 1;
    if (sort === "name") return sign * byName(a, b);
    const d = key(a) - key(b);
    return d !== 0 ? sign * d : byName(a, b);
  });
}

/** Case-insensitive, numbers in order ("a2" < "a10"); a stable, locale-independent stand-in for Intl.Collator. */
export function compareNames(a: string, b: string): number {
  const ta = tokens(a.toLowerCase());
  const tb = tokens(b.toLowerCase());
  for (let i = 0; i < Math.min(ta.length, tb.length); i++) {
    const x = ta[i]!;
    const y = tb[i]!;
    const nx = /^\d/.test(x);
    const ny = /^\d/.test(y);
    if (nx && ny) {
      const d = Number(x) - Number(y);
      if (d !== 0) return d < 0 ? -1 : 1;
      continue;
    }
    if (x !== y) return x < y ? -1 : 1;
  }
  return ta.length - tb.length === 0 ? (a < b ? -1 : a > b ? 1 : 0) : ta.length < tb.length ? -1 : 1;
}

function tokens(s: string): string[] {
  return s.match(/\d+|\D+/g) ?? [];
}

// ---------- Names ----------

/** Splits "Budget Q4.xlsx" into ["Budget Q4", ".xlsx"]; names without an extension (or dotfiles) keep it all. */
export function splitName(name: string): [string, string] {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return [name, ""];
  return [name.slice(0, dot), name.slice(dot)];
}

/** Google Drive's copy name: "Copy of Budget Q4.xlsx". */
export function copyName(name: string): string {
  return clip(`Copy of ${name}`);
}

/**
 * The name sync clients give the losing side of a conflict, beside the original:
 * "Budget Q4 (conflicted copy from MacBook Pro 2026-10-27).xlsx". `date` is YYYY-MM-DD in the device's time zone.
 */
export function conflictedCopyName(name: string, device: string, date: string): string {
  const [stem, ext] = splitName(name);
  const tag = device.trim() === "" ? ` (conflicted copy ${date})` : ` (conflicted copy from ${device.trim()} ${date})`;
  return clip(stem.slice(0, Math.max(1, 255 - tag.length - ext.length)) + tag + ext);
}

function clip(s: string): string {
  return s.length > 255 ? s.slice(0, 255) : s;
}

/** A name people may give an item: trimmed, 1–255 characters, no "/" or control characters, not "." or "..". */
export function validDriveName(raw: string): string | null {
  const name = raw.trim();
  if (name === "" || name === "." || name === ".." || name.length > 255) return null;
  for (const ch of name) {
    const code = ch.charCodeAt(0);
    if (code < 0x20 || code === 0x7f || ch === "/" || ch === "\\") return null;
  }
  return name;
}

/**
 * Uploading a file whose name is already in the folder: Drive asks "Replace existing file" (a new version) or "Keep
 * all items separate". Returns the id of the file it would replace (the newest one with that exact name), if any.
 */
export function sameNameFile(siblings: Pick<DriveItem, "id" | "kind" | "name" | "modifiedAt">[], name: string): string | undefined {
  let best: Pick<DriveItem, "id" | "modifiedAt"> | undefined;
  for (const s of siblings) if (s.kind === "file" && s.name === name && (best === undefined || s.modifiedAt > best.modifiedAt)) best = s;
  return best?.id;
}

// ---------- Moves ----------

/**
 * Why moving `itemId` into `targetId` (undefined = My files root) is not allowed, or null when it is. `targetPath` is
 * the target folder and the folders above it (any order). A folder can't go into itself or below itself.
 */
export function moveProblem(itemId: string, currentParentId: string | undefined, targetId: string | undefined, targetPath: string[]): MessageDescriptor | null {
  if (targetId === currentParentId) return msg("drive.move.sameFolder");
  if (targetId === itemId || targetPath.includes(itemId)) return msg("drive.move.intoItself");
  return null;
}

// ---------- Storage ----------

/** Share of the quota used, 0–1 (a full or over-full pool is 1; no quota = 0). */
export function storageFraction(used: number, quota: number): number {
  if (quota <= 0) return 0;
  return Math.min(1, Math.max(0, used / quota));
}

/** "ok" below 90%, "warning" from 90%, "full" at 100% (uploads to Files stop). */
export function storageLevel(used: number, quota: number): "ok" | "warning" | "full" {
  const f = storageFraction(used, quota);
  if (quota > 0 && used >= quota) return "full";
  return f >= 0.9 ? "warning" : "ok";
}

// ---------- General access and public links ----------

/** One choice of the share dialog's "General access" menu. */
export type LinkScopeOption = {
  scope: DriveLinkScope;
  /** Shown but not choosable ("Anyone with the link" while the workspace's `publicFiles` policy blocks it). */
  disabled: boolean;
  /** The line under the label: what the scope means, or why it is disabled. */
  hint: MessageDescriptor;
};

/**
 * General access, like Drive: Restricted, the workspace, Anyone with the link. "Anyone" stays listed when the policy
 * blocks it, disabled with the reason ("off": turned off by an admin; "admins": only admins can do this). The current
 * scope is never disabled, so a link made before the policy changed still shows as chosen.
 */
export function linkScopeOptions(current: DriveLinkScope, canLinkAnyone: boolean, anyoneBlocked: DriveAnyoneBlocked | undefined): LinkScopeOption[] {
  const anyoneDisabled = !canLinkAnyone && current !== "anyone";
  return [
    { scope: "restricted", disabled: false, hint: linkScopeHint("restricted") },
    { scope: "workspace", disabled: false, hint: linkScopeHint("workspace") },
    {
      scope: "anyone",
      disabled: anyoneDisabled,
      hint: !anyoneDisabled ? linkScopeHint("anyone") : anyoneBlocked === "off" ? msg("drive.share.blocked.off") : msg("drive.share.blocked.admins"),
    },
  ];
}

/** What a general-access scope lets people do (the line under it). */
export function linkScopeHint(scope: DriveLinkScope): MessageDescriptor {
  switch (scope) {
    case "restricted":
      return msg("drive.share.restrictedHint");
    case "workspace":
      return msg("drive.share.anyoneHint");
    case "anyone":
      return msg("drive.share.anyoneLinkHint");
  }
}

/** The link role (Viewer / Commenter / Editor) applies to workspace links and to members who open an anyone link. */
export function linkHasRole(scope: DriveLinkScope): boolean {
  return scope !== "restricted";
}

const PUBLIC_DRIVE_PATH = /^\/f\/([A-Za-z0-9_-]{16,128})\/?$/;

/** The token of an "Anyone with the link" Files URL (`/f/<token>`), or undefined for any other path. */
export function publicDriveTokenFromPath(path: string): string | undefined {
  return PUBLIC_DRIVE_PATH.exec(path)?.[1];
}

/** What a row of a public folder does: folders browse, files preview and download, pages and databases need Bundle. */
export function publicItemAction(kind: DriveItemKind): "browse" | "file" | "signIn" {
  if (kind === "folder") return "browse";
  if (kind === "file") return "file";
  return "signIn";
}
